import React, { useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { Col, Row } from 'react-bootstrap';
import { MdDelete } from 'react-icons/md';

import { FileUploader } from 'react-drag-drop-files';

import {
  deleteAdditionalDocument,
  fetchAdditionalDocuments,
  getAdditionalDocumentUrl,
  uploadAdditionalDocument,
} from './operations';
import { ModalConfirmation } from '../../../components';
import useIsMobileScreen from '../../../hooks/useCheckIsMobile';

const ACCEPTED_FILE_TYPES = ['PDF', 'DOC', 'DOCX'];

const UploadButton = () => (
  <div
    style={{
      border: '2px dashed #da0f2e',
      borderRadius: '4px',
      padding: '6px 14px',
      color: '#da0f2e',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
    UPLOAD OR DROP A FILE RIGHT HERE
  </div>
);

const AdditionalDocuments = ({ dqfId, isServiceTeam, currentUserEmail, currentUserFullName }) => {
  const isMobile = useIsMobileScreen();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [typeError, setTypeError] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadDocuments = useCallback(async () => {
    const items = await fetchAdditionalDocuments(dqfId);
    setDocuments(items);
  }, [dqfId]);

  useEffect(() => {
    if (dqfId) loadDocuments();
  }, [dqfId, loadDocuments]);

  const handleUpload = async file => {
    setUploadError('');
    setTypeError(false);
    setUploading(true);
    const result = await uploadAdditionalDocument(dqfId, file, currentUserEmail, currentUserFullName);
    setUploading(false);
    if (result) {
      setDocuments(prev => [...prev, result]);
    } else {
      setUploadError('Upload failed. Please check your connection and try again.');
    }
  };

  const handleDelete = async () => {
    const { documentId, storageKey } = pendingDelete;
    setPendingDelete(null);
    const ok = await deleteAdditionalDocument(documentId, storageKey);
    if (ok) {
      setDocuments(prev => prev.filter(d => d.id !== documentId));
    }
  };

  const handleDownload = async storageKey => {
    const url = await getAdditionalDocumentUrl(storageKey);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.click();
    }
  };

  return (
    <div style={{ width: '100%', marginTop: '24px', marginBottom: '24px' }}>
      <Row className="pt-1 mb-2 align-items-center justify-content-between">
        <Col className="font-weight-bold">Additional Documents</Col>
        {isServiceTeam && (
          <Col xs="auto">
            {uploading ? (
              <div style={{ fontSize: '12px', color: '#53565A' }}>Uploading...</div>
            ) : (
              <FileUploader
                handleChange={handleUpload}
                name="additional-doc"
                types={ACCEPTED_FILE_TYPES}
                onTypeError={() => setTypeError(true)}>
                <UploadButton />
              </FileUploader>
            )}
            {typeError && (
              <div style={{ fontSize: '12px', color: '#c8102e', marginTop: 4 }}>
                File type not supported. Use PDF, DOC, or DOCX.
              </div>
            )}
          </Col>
        )}
      </Row>
      {uploadError && (
        <div style={{ fontSize: '12px', color: '#c8102e', marginBottom: 8 }}>{uploadError}</div>
      )}

      {isMobile && !isServiceTeam ? (
        <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
          {!documents.length ? (
            <div style={{ padding: '8px 12px', color: '#53565A', fontSize: '14px' }}>
              None
            </div>
          ) : (
            documents.map(doc => (
              <div key={doc.id} style={{ padding: '10px 12px', borderBottom: '1px solid #dee2e6' }}>
                <button
                  type="button"
                  onClick={() => handleDownload(doc.documentName)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#c8102e',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    display: 'block',
                    textAlign: 'left',
                  }}>
                  {doc.originalFileName}
                </button>
                <div style={{ fontSize: '12px', color: '#53565A', marginTop: 2 }}>
                  {`By ${doc.uploadedByFullName} ${dayjs(doc.createdAt).format('MM/DD/YYYY')}`}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #dee2e6',
            borderRadius: 4,
          }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Document Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Upload By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Uploaded Date</th>
                {isServiceTeam && <th style={{ padding: '8px 12px', width: 40 }} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {!documents.length ? (
                <tr>
                  <td
                    colSpan={isServiceTeam ? 4 : 3}
                    style={{ padding: '8px 12px', color: '#53565A' }}>
                    None
                  </td>
                </tr>
              ) : (
                documents.map(doc => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <button
                        type="button"
                        onClick={() => handleDownload(doc.documentName)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#c8102e',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '14px',
                        }}>
                        {doc.originalFileName}
                      </button>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#53565A' }}>{doc.uploadedByFullName}</td>
                    <td style={{ padding: '8px 12px', color: '#53565A' }}>{dayjs(doc.createdAt).format('MM/DD/YYYY')}</td>
                    {isServiceTeam && (
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setPendingDelete({ documentId: doc.id, storageKey: doc.documentName, fileName: doc.originalFileName })}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#c8102e',
                          }}
                          title="Delete document"
                          aria-label={`Delete ${doc.originalFileName}`}>
                          <MdDelete size={18} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {pendingDelete && (
        <ModalConfirmation
          title="Delete Document"
          body={`Are you sure you want to permanently delete "${pendingDelete.fileName}"? This cannot be undone.`}
          primaryButtonTitle="Delete"
          primaryButton
          optionalClose
          secondaryButtonTitle="Cancel"
          onClick={handleDelete}
          secondaryOnClick={() => setPendingDelete(null)}
          isOpen={!!pendingDelete}
        />
      )}
    </div>
  );
};

export default AdditionalDocuments;
